import { useEffect, useRef } from 'react';
import { Animated, View, type ViewStyle } from 'react-native';
import Svg, { Defs, LinearGradient, Path, Stop } from 'react-native-svg';
import { useReducedMotion } from '../../hooks/useReducedMotion';
import { colors } from '../../theme/colors';

interface SparklineProps {
  data: number[];
  width?: number;
  height?: number;
  color?: string;
  strokeWidth?: number;
  showGradient?: boolean;
  gradientOpacity?: number;
  animate?: boolean;
  animationDuration?: number;
  style?: ViewStyle;
}

function generatePath(data: number[], width: number, height: number, padding = 2): string {
  if (data.length < 2) return '';

  const minValue = Math.min(...data);
  const maxValue = Math.max(...data);
  const range = maxValue - minValue || 1;

  const effectiveHeight = height - padding * 2;
  const effectiveWidth = width - padding * 2;

  const points = data.map((value, index) => {
    const x = padding + (index / (data.length - 1)) * effectiveWidth;
    const y = padding + effectiveHeight - ((value - minValue) / range) * effectiveHeight;
    return { x, y };
  });

  let path = `M ${points[0].x} ${points[0].y}`;

  for (let i = 0; i < points.length - 1; i++) {
    const current = points[i];
    const next = points[i + 1];
    const controlX = (current.x + next.x) / 2;
    path += ` Q ${controlX} ${current.y} ${controlX} ${(current.y + next.y) / 2}`;
  }

  const lastPoint = points.at(-1)!;
  path += ` L ${lastPoint.x} ${lastPoint.y}`;

  return path;
}

function generateAreaPath(data: number[], width: number, height: number, padding = 2): string {
  if (data.length < 2) return '';

  const linePath = generatePath(data, width, height, padding);
  const effectiveWidth = width - padding * 2;

  return `${linePath} L ${padding + effectiveWidth} ${height} L ${padding} ${height} Z`;
}

export function Sparkline({
  data,
  width = 80,
  height = 24,
  color = colors.primary.neon,
  strokeWidth = 1.5,
  showGradient = true,
  gradientOpacity = 0.3,
  animate = true,
  animationDuration = 600,
  style,
}: Readonly<SparklineProps>) {
  const reducedMotion = useReducedMotion();
  const fadeAnim = useRef(new Animated.Value(0)).current;
  const shouldAnimate = animate && !reducedMotion;

  useEffect(() => {
    if (shouldAnimate) {
      fadeAnim.setValue(0);
      Animated.timing(fadeAnim, {
        toValue: 1,
        duration: animationDuration,
        useNativeDriver: true,
      }).start();
    } else {
      fadeAnim.setValue(1);
    }
  }, [shouldAnimate, animationDuration, fadeAnim]);

  if (!data || data.length < 2) {
    return <View style={[{ width, height }, style]} />;
  }

  const linePath = generatePath(data, width, height);
  const areaPath = generateAreaPath(data, width, height);
  const gradientId = `sparkline-gradient-${Math.random().toString(36).substring(2, 11)}`;
  const minVal = Math.min(...data);
  const maxVal = Math.max(...data);
  const lastVal = data.at(-1);
  const chartLabel = `Chart: ${data.length} points, range ${minVal}-${maxVal}, current ${lastVal}`;

  return (
    <Animated.View
      style={[{ width, height, opacity: fadeAnim }, style]}
      accessibilityRole="image"
      accessibilityLabel={chartLabel}
    >
      <Svg width={width} height={height} viewBox={`0 0 ${width} ${height}`}>
        <Defs>
          <LinearGradient id={gradientId} x1="0%" y1="0%" x2="0%" y2="100%">
            <Stop offset="0%" stopColor={color} stopOpacity={gradientOpacity} />
            <Stop offset="100%" stopColor={color} stopOpacity={0} />
          </LinearGradient>
        </Defs>
        {showGradient && <Path d={areaPath} fill={`url(#${gradientId})`} />}
        <Path
          d={linePath}
          stroke={color}
          strokeWidth={strokeWidth}
          fill="none"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </Svg>
    </Animated.View>
  );
}

export type { SparklineProps };
